import React, { useEffect, useState } from "react";
import { Edit, PlusCircle, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import AddScriptModal from "../AdminPannel/AddScriptModal";
import ConfirmDeleteModal from "../ModelUI/ConfirmDeleteModal/ConfirmDeleteModal";
import AuthService from "../../services/Auth.service";
import "./ScriptManagementContent.css";

const SCRIPT_TYPE = {
  BASE: "Base",
  DEPENDENT: "Dependent",
  UNIQUE: "Unique",
  USER_GENERATED:"User Generated"
};

export default function ScriptManagementContent() {
  const [loading, setLoading] = useState(false);
  const [baseScripts, setBaseScripts] = useState([]);
  const [uniqueScripts, setUniqueScripts] = useState([]);
  const [userGeneratedScripts, setUserGeneratedScripts] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [parentId, setParentId] = useState(null);
  const [scriptType, setScriptType] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchScripts = async () => {
    try {
      setLoading(true);
      const res = await AuthService.getScriptList();

      if (res.status === 200) {
        setBaseScripts(res.data.base_scripts || []);
        setUniqueScripts(res.data.unique_scripts || []);
        setUserGeneratedScripts(res.data.user_generated || []);
      } else {
        toast.error("Failed to load scripts");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error loading scripts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScripts();
  }, []);

  const handleAdd = async (parentId, type) => {
    setIsEditing(false);
    setParentId(parentId);
    setScriptType(type);
    setSelectedScript(null);

    if (type === SCRIPT_TYPE.DEPENDENT && parentId) {
      try {
        setLoading(true);

        const res = await AuthService.getScriptById(parentId);
        const parentData = res?.data || {};

      } catch (err) {
        toast.error("Failed to copy parent script");
        return;
      } finally {
        setLoading(false);
      }
    }

    setShowModal(true);
  };


  const handleEdit = (script, parentId = null) => {
    setSelectedScript(script);
    setIsEditing(true);

    if (parentId) {
      setScriptType(SCRIPT_TYPE.DEPENDENT);
      setParentId(parentId);
    } else if (script.script_type === SCRIPT_TYPE.UNIQUE) {
      setScriptType(SCRIPT_TYPE.UNIQUE);
      setParentId(null);
    } else {
      setScriptType(SCRIPT_TYPE.BASE);
      setParentId(null);
    }

    setShowModal(true);
  };


  const handleSaveScript = async (payload) => {
    try {
      const finalPayload = {
        ...payload,
        script_type: scriptType,
        parent_id: scriptType === SCRIPT_TYPE.DEPENDENT ? parentId : null,
      };

      let res;
      if (isEditing && selectedScript) {
        res = await AuthService.updateScript(selectedScript.id, finalPayload);

        if (res.status === 200) {
          toast.success("Script updated successfully");

        } else {
          toast.error("Failed to update script");
        }
      } else {
        res = await AuthService.createNewScript(finalPayload);

        if (res.status === 201 || res.status === 200) {
          toast.success("Script added successfully");
        } else {
          toast.error("Failed to add script");
        }
      }
      setShowModal(false);
      fetchScripts();
    } catch {
      toast.error("Save failed");
    }
  };


  const handleDelete = (script) => {
    setDeleteTarget(script);
    setShowDeleteModal(true);
  };


  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      const res = await AuthService.deleteScript(deleteTarget.id);
      
      if (res.status === 200 || res.status === 204) {
        toast.success("Script deleted");
        fetchScripts();
      } else {
        toast.error("Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Delete error");
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };


  return (
    <div className="script-management-container">
      <section className="section">
        <h2> Base Scripts
          <PlusCircle
            size={24}
            color="#1dc04bff"
            title="Add Unique Script"
            onClick={() => handleAdd(null, SCRIPT_TYPE.BASE)}
          />
        </h2>
        <div className="card-container">
          {baseScripts.map((script) => (
            <div className="script-card" key={script.id}>
              <div className="script-header">
                <h3>{script.script_name}</h3>
                <div className="actions">
                  <Edit
                    size={18}
                    color="#f4a261"
                    onClick={() => handleEdit(script)}
                  />
                  <Trash2
                    size={16}
                    color="#e63946"
                    onClick={() => handleDelete(script)}
                  />

                  <PlusCircle
                    size={18}
                    color="#1dc04bff"
                    title="Add Child Script"
                    onClick={() =>
                      handleAdd(script.id, SCRIPT_TYPE.DEPENDENT)
                    }
                  />
                </div>
              </div>

              {script.child_scripts?.length > 0 && (
                <div className="company-list">
                  {script.child_scripts.map((child) => (
                    <div className="company-item" key={child.id}>
                      {child.script_name}
                      <div className="company-actions">
                        <Edit
                          size={16}
                          color="#f4a261"
                          onClick={() =>
                            handleEdit(child, script.id)
                          }
                        />
                        <Trash2
                          size={16}
                          color="#e63946"
                          onClick={() => handleDelete(child)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <h2 className="section-header">
          <span>Unique Scripts</span>
          <PlusCircle
            size={24}
            color="#1dc04bff"
            title="Add Unique Script"
            onClick={() => handleAdd(null, SCRIPT_TYPE.UNIQUE)}
          />
        </h2>

        <div className="card-container">
          {uniqueScripts.map((script) => (
            <div className="script-card unique-card" key={script.id}>
              <div className="script-header">
                <h3>{script.script_name}</h3>
                <div className="actions">
                  <Edit
                    size={18}
                    color="#f4a261"
                    onClick={() => handleEdit(script, SCRIPT_TYPE.USER_GENERATED)}
                  />
                  <Trash2
                    size={18}
                    color="#e63946"
                    onClick={() => handleDelete(script)}
                  />
                </div>
              </div>
              <p>{script.script_description}</p>
            </div>
          ))}
        </div>
      </section>
       <section className="section">
        <h2 className="section-header">
          <span>Users Scripts</span>
          <PlusCircle
            size={24}
            color="#1dc04bff"
            title="Add Unique Script"
            onClick={() => handleAdd(null, SCRIPT_TYPE.USER_GENERATED)}
          />
        </h2>

        <div className="card-container">
          {userGeneratedScripts.map((script) => (
            <div className="script-card unique-card" key={script.id}>
              <div className="script-header">
                <h3>{script.script_name}</h3>
                <div className="actions">
                  <Edit
                    size={18}
                    color="#f4a261"
                    onClick={() => handleEdit(script, SCRIPT_TYPE.UNIQUE)}
                  />
                  <Trash2
                    size={18}
                    color="#e63946"
                    onClick={() => handleDelete(script)}
                  />
                </div>
              </div>
              <p>{script.script_description}</p>
            </div>
          ))}
        </div>
      </section>

      {showModal && (
        <AddScriptModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSaveScript}
          scriptToEdit={isEditing ? selectedScript : null}
        />

      )}

      {showDeleteModal && (
        <ConfirmDeleteModal
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          itemName={deleteTarget?.script_name || "this script"}
        />
      )}
    </div>
  );
}
